import { useEffect, useRef, useState } from 'react';
import {
  FINAL_RETURN_STATES,
  PAYMENT_RETURN_SETTLED_EVENT,
  RETURN_POLL_SCHEDULE_MS,
  clearPaymentReturn,
  isPaymentReturnFor,
} from '../../views/restaurant-onboarding/phases/payoutsPhaseHelpers';

/**
 * The restaurant the return poll is running for, if any. The Online payment step
 * and the KYC banner are both on screen after a return and both want the fresh
 * status; one polls, the other waits for it to finish instead of doubling every
 * read.
 */
let pollingFor = null;

/**
 * A waiting reader stops waiting after this long even if no poll ever finishes —
 * the one polling may have unmounted, and a banner hidden for good is worse than
 * one shown a moment early.
 */
const WAIT_LIMIT_MS = RETURN_POLL_SCHEDULE_MS.at(-1) + 5000;

/**
 * Whether the owner has just come back from the provider's hosted onboarding for
 * this restaurant and its answer is still being read.
 *
 * Back from Stripe or Adyen, the status only moves once the provider's webhook
 * lands, which is usually a few seconds after the redirect. So the status is read
 * on a fixed schedule for ten seconds — stopping early on a final answer — and
 * `true` is returned throughout, which is what the step shows a loader for and
 * what keeps the KYC banner from asking the owner to do what they just did.
 * When it is over the marker leaves the URL and every `usePaymentSetup` reloads.
 */
export default function usePaymentReturnPoll(companyId, reload) {
  const [checking, setChecking] = useState(
    () => Boolean(companyId) && isPaymentReturnFor(window.location.search, companyId),
  );
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  // The restaurant is often not known on the first render of the layout.
  useEffect(() => {
    if (companyId && isPaymentReturnFor(window.location.search, companyId)) {
      setChecking(true);
    }
  }, [companyId]);

  useEffect(() => {
    if (!checking) {
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    if (pollingFor === companyId) {
      // Its own read first: the status this instance renders from would otherwise
      // show the pre-return state for the moment its reload takes to land.
      const stopWaiting = async () => {
        try {
          await reloadRef.current?.();
        } catch {
          // Shown as it stands; the next read will correct it.
        }

        if (!cancelled) {
          setChecking(false);
        }
      };

      window.addEventListener(PAYMENT_RETURN_SETTLED_EVENT, stopWaiting);
      timer = setTimeout(stopWaiting, WAIT_LIMIT_MS);

      return () => {
        cancelled = true;
        clearTimeout(timer);
        window.removeEventListener(PAYMENT_RETURN_SETTLED_EVENT, stopWaiting);
      };
    }

    pollingFor = companyId;
    let attempt = 0;
    let finished = false;

    const finish = () => {
      finished = true;
      pollingFor = null;
      clearPaymentReturn();
      setChecking(false);
      window.dispatchEvent(new Event(PAYMENT_RETURN_SETTLED_EVENT));
    };

    const poll = async () => {
      let loaded = null;

      try {
        loaded = await reloadRef.current?.();
      } catch {
        // A failed read is one fewer answer, not a reason to stop the schedule.
      }

      if (cancelled) {
        return;
      }

      attempt += 1;

      if (FINAL_RETURN_STATES.includes(loaded?.status) || attempt >= RETURN_POLL_SCHEDULE_MS.length) {
        finish();

        return;
      }

      timer = setTimeout(poll, RETURN_POLL_SCHEDULE_MS[attempt] - RETURN_POLL_SCHEDULE_MS[attempt - 1]);
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);

      if (pollingFor === companyId) {
        pollingFor = null;
      }

      // Left mid-schedule: whoever is waiting on this poll reads for itself.
      if (!finished) {
        window.dispatchEvent(new Event(PAYMENT_RETURN_SETTLED_EVENT));
      }
    };
  }, [checking, companyId]);

  return checking;
}
